import { Link } from "react-router-dom";
import { Building2, UserPlus, CheckCircle, Headphones, Users, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import FloatingParticles from "@/components/ui/floating-particles";

const CTA = () => {
  const benefits = [
    { icon: CheckCircle, text: "Free Basic Listing" },
    { icon: Headphones, text: "24/7 Support" },
    { icon: Users, text: "10L+ Monthly Visitors" },
    { icon: Award, text: "100% Verified Leads" },
  ];

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-3xl overflow-hidden"
        >
          {/* Background */}
          <div className="absolute inset-0" style={{ background: 'var(--ui-gradient-hero)' }} />
          
          <motion.div
            className="absolute inset-0 opacity-20"
            style={{ background: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.4) 0%, transparent 50%)' }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          
          <FloatingParticles />

          {/* ECG line decoration */}
          <div className="absolute inset-0 opacity-[0.06]">
            <svg viewBox="0 0 1440 200" className="absolute top-1/2 w-full -translate-y-1/2" preserveAspectRatio="none">
              <path
                d="M0,100 L200,100 L220,40 L240,160 L260,80 L280,120 L300,100 L500,100 L520,40 L540,160 L560,80 L580,120 L600,100 L1440,100"
                fill="none" stroke="white" strokeWidth="2"
              />
            </svg>
          </div>

          <div className="relative z-10 py-10 sm:py-14 md:py-16 lg:py-20 px-4 sm:px-6 md:px-12 lg:px-16">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-pill px-5 py-2.5 mb-8"
              >
                <Building2 className="w-5 h-5 text-white" />
                <span className="text-white/90 font-medium">For Healthcare Providers</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight"
              >
                Are You a{' '}
                <span className="relative inline-block">
                  <span>Healthcare Provider?</span>
                  <motion.span
                    className="absolute -bottom-2 left-0 right-0 h-2 bg-healing-green/50 rounded-pill"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                  />
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-sm sm:text-base md:text-lg lg:text-xl text-white/85 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto px-2"
              >
                Join India's fastest growing healthcare directory. List your hospital, clinic, pharmacy or diagnostic center and reach thousands of patients.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/auth">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      size="lg"
                      className="bg-white text-primary hover:bg-white/90 font-semibold text-base px-8 rounded-pill shadow-xl shadow-black/10 animate-glow"
                    >
                      <Building2 className="w-5 h-5 mr-2" />
                      Register Your Services
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/auth">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/10 font-semibold text-base px-8 rounded-pill bg-transparent"
                    >
                      <UserPlus className="w-5 h-5 mr-2" />
                      Become a Partner
                    </Button>
                  </motion.div>
                </Link>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="mt-10 sm:mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6"
              >
                {benefits.map((benefit, idx) => (
                  <motion.div
                    key={benefit.text}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7 + idx * 0.1 }}
                    className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-white/90 text-sm font-medium text-center">{benefit.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;
